import { Maze, type Action, type AgentState, type AgentView, type InputResult, type MatchEvent, type MazeWire, type Mirror } from '@qoobd/rules';

/**
 * The browser's stand-in for `Match` during online play. The real match runs on the server;
 * this holds only what the server sends this player: their own view (self, sensors, the rival
 * when in sight, seals, caches, scorch, the core once seen) and the events they may see.
 * It answers the same questions the renderer and controller ask a local `Match`.
 */
export class RemoteMatch {
  readonly maze: Maze;
  /** The cube's mirror rooms (public decoration, sent by the server). */
  readonly mirrors: readonly Mirror[];
  private view: AgentView | null = null;
  private events: MatchEvent[] = [];
  /** Server match time minus performance.now(), from the freshest-looking sample. */
  private offset: number | null = null;
  private readonly sendInput: (action: Action) => void;

  constructor(wire: MazeWire, sendInput: (action: Action) => void, mirrors: readonly Mirror[] = []) {
    this.maze = Maze.fromWire(wire);
    this.mirrors = mirrors;
    this.sendInput = sendInput;
  }

  get ready(): boolean {
    return this.view !== null;
  }

  /** Apply a `state` message from the server. */
  apply(t: number, view: AgentView, events: MatchEvent[]): void {
    const sample = t - performance.now();
    // The least-delayed message gives the best estimate; let it relax slowly in case of drift.
    this.offset = this.offset === null ? sample : Math.max(sample, this.offset - 2);
    this.view = view;
    // Several messages can arrive between frames, so by the time the controller reads an
    // actionStarted event the latest view may show the action already finished. Pin the
    // action as it was when this message was sent.
    for (const e of events) {
      if (e.type === 'actionStarted' && e.agent === 'player' && view.self.active && view.self.active.startedAt === e.at) {
        (e as MatchEvent & { detail?: unknown }).detail = view.self.active;
      }
    }
    this.events.push(...events);
  }

  /** The server's match clock, as best this browser can tell. */
  now(): number {
    return performance.now() + (this.offset ?? 0);
  }

  agent(id: string): AgentState | undefined {
    const v = this.view;
    if (!v) return undefined;
    if (id === 'player') return v.self as AgentState;
    // Only what the view shows of the rival: where, which way, and a step in progress.
    const r = v.rival;
    if (!r) return undefined;
    return {
      id: 'rival',
      room: r.room,
      orientation: r.orientation,
      item: 'rock', // never known online; only revealed by the server at the end
      gear: { emp: 0, bulkhead: 0 },
      active: r.moving ? { kind: 'step', from: r.moving.from, to: r.moving.to, startedAt: r.moving.startedAt, endsAt: r.moving.endsAt } : null,
      jammedUntil: 0,
      justPassed: null,
      steps: 0,
      actions: 0,
      bonks: 0,
    } as AgentState;
  }

  viewFor(_id: string): AgentView {
    return this.view!;
  }

  sensorsFor(_id: string): AgentView['sensors'] {
    return this.view!.sensors;
  }

  takeEvents(): MatchEvent[] {
    const e = this.events;
    this.events = [];
    return e;
  }

  advance(_now: number): void {
    /* the server advances the match */
  }

  /**
   * Inputs go to the server. A press during a lock this browser already knows about is refused
   * here, so the HUD flashes at once; the server refuses any it catches that we didn't.
   */
  input(_id: string, action: Action, now: number): InputResult {
    const self = this.view?.self;
    if (!self) return { ok: false, reason: 'locked' };
    if (self.active && now < self.active.endsAt) return { ok: false, reason: 'locked' };
    if (action.kind === 'swap' && action.item === self.item) return { ok: false, reason: 'sameItem' };
    if (action.kind === 'emp' && self.gear.emp < 1) return { ok: false, reason: 'noCharge' };
    if (action.kind === 'bulkhead' && self.gear.bulkhead < 1) return { ok: false, reason: 'noCharge' };
    this.sendInput(action);
    return { ok: true, endsAt: now };
  }
}
